import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import moment from "moment";
import { toast } from "react-toastify";
import yardService from "services/yardService";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";
import { READ_ONLY_MESSAGE, useShiftPermissions } from "shifts/permissions";
import { YardInstanceResponse, YardLookupsResponse } from "yard/models";

// Yard Daily instance detail — replaces the legacy Logistrics YardDailyDetail page.
//
// Two legacy defects are fixed here: control number and short key now actually persist (the
// legacy update path silently dropped them), and they are written to this row only rather
// than being propagated across sibling sub-shifts.

const STATUSES = ["Open", "Closed", "Cancelled"];
const TIME_PATTERN = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;

const emptyLookups: YardLookupsResponse = {
  drivers: [],
  vehicles: [],
  clients: [],
  shiftTypes: [],
  driverChangeReasons: [],
  vehicleChangeReasons: [],
};

const YardDailyDetail = () => {
  const { yardDailyId } = useParams();
  const navigate = useNavigate();
  const { canEditYardDaily } = useShiftPermissions();
  const [params] = useSearchParams();

  const [instance, setInstance] = useState<YardInstanceResponse | null>(null);
  const [original, setOriginal] = useState<YardInstanceResponse | null>(null);
  const [lookups, setLookups] = useState<YardLookupsResponse>(emptyLookups);
  const [driverReasonId, setDriverReasonId] = useState<number | null>(null);
  const [vehicleReasonId, setVehicleReasonId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const backTo = `/shifts/logistrics/daily?hub=${params.get("hub") ?? ""}&date=${params.get("date") ?? ""}`;

  const load = useCallback(async () => {
    const id = Number(yardDailyId);
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await yardService.getInstance(id);
      if (!result.found) {
        toast.error("That shift no longer exists.");
        return;
      }
      setInstance(result);
      setOriginal(result);
      setDriverReasonId(null);
      setVehicleReasonId(null);
      setLookups(await yardService.getLookups(result.hubId));
    } catch {
      toast.error("Could not load the shift.");
    } finally {
      setIsLoading(false);
    }
  }, [yardDailyId]);

  useEffect(() => {
    load();
  }, [load]);

  if (isLoading) return <Box sx={{ px: 1 }}>Loading…</Box>;
  if (!instance || !original) return <Box sx={{ px: 1 }}>Shift not found.</Box>;

  // Two independent reasons a shift cannot be edited: it has been approved (nobody may edit
  // it here), or the user only has view rights. Both disable the same inputs; they are kept
  // apart so the banner can say which one applies.
  const lacksEditPermission = !canEditYardDaily;
  const readOnly = instance.isApproved || lacksEditPermission;
  const driverChanged = instance.driverId !== original.driverId;
  const vehicleChanged = instance.vehicleId !== original.vehicleId;
  const reopening = original.status !== "Open" && instance.status === "Open";

  const set = (changes: Partial<YardInstanceResponse>) =>
    setInstance((current) => (current ? { ...current, ...changes } : current));

  const save = async () => {
    if (instance.timeStart && !TIME_PATTERN.test(instance.timeStart)) {
      toast.error("Start time must be HH:mm.");
      return;
    }
    if (instance.timeEnd && !TIME_PATTERN.test(instance.timeEnd)) {
      toast.error("End time must be HH:mm.");
      return;
    }
    if (driverChanged && !driverReasonId) {
      toast.error("Changing the driver needs a change reason.");
      return;
    }
    if (vehicleChanged && !vehicleReasonId) {
      toast.error("Changing the vehicle needs a change reason.");
      return;
    }

    setIsSaving(true);
    try {
      const result = await yardService.saveInstance({
        yardDailyId: instance.yardDailyId,
        timeStart: instance.timeStart,
        timeEnd: instance.timeEnd,
        controlNumber: instance.controlNumber,
        shortKey: instance.shortKey,
        driverId: instance.driverId,
        vehicleId: instance.vehicleId,
        clientId: instance.clientId,
        shiftTypeId: instance.shiftTypeId,
        status: instance.status,
        driverChangeReasonId: driverChanged ? driverReasonId : null,
        vehicleChangeReasonId: vehicleChanged ? vehicleReasonId : null,
        // The server re-checks the transition; this only says the UI believes it is allowed.
        canReopen: true,
      });

      if (result.success) {
        toast.success(result.message || "Saved.");
        await load();
      } else {
        toast.error(result.message || "Could not save the shift.");
      }
    } catch {
      toast.error("Could not save the shift.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ px: 1, maxWidth: 1100 }}>
      <Button component={Link} to={backTo} startIcon={<ArrowBackIcon />} size="small" sx={{ mb: 1 }}>
        Back to Yard Daily
      </Button>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h5">
          {instance.shiftName} — {moment(instance.date).format("ddd MM/DD/YYYY")}
        </Typography>
        {instance.isApproved && <Chip label="Approved" color="success" size="small" />}
        {instance.isBnsfInactive && <Chip label="BNSF inactive" color="warning" size="small" variant="outlined" />}
      </Stack>
      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        {instance.location} · Shift #{instance.yardDailyId}
        {instance.templateTimeStart && ` · scheduled ${instance.templateTimeStart}–${instance.templateTimeEnd}`}
      </Typography>

      {instance.isApproved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This shift has been approved{instance.approvedBy ? ` by ${instance.approvedBy}` : ""} and is read-only.
        </Alert>
      )}

      {lacksEditPermission && !instance.isApproved && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {READ_ONLY_MESSAGE}
        </Alert>
      )}

      {reopening && !readOnly && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Re-opening a closed or cancelled shift needs the reopen permission. The save will be rejected without it.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Start time"
              value={instance.timeStart}
              onChange={(e) => set({ timeStart: e.target.value })}
              disabled={readOnly}
              placeholder="HH:mm"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="End time"
              value={instance.timeEnd}
              onChange={(e) => set({ timeEnd: e.target.value })}
              disabled={readOnly}
              placeholder="HH:mm"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Control number"
              value={instance.controlNumber}
              onChange={(e) => set({ controlNumber: e.target.value })}
              disabled={readOnly}
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <TextField
              label="Short key"
              value={instance.shortKey}
              onChange={(e) => set({ shortKey: e.target.value.slice(0, 6) })}
              disabled={readOnly}
              size="small"
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <LookupSelect
              label="Driver"
              value={instance.driverId}
              options={lookups.drivers}
              disabled={readOnly}
              onChange={(v) => set({ driverId: v })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LookupSelect
              label="Vehicle"
              value={instance.vehicleId}
              options={lookups.vehicles}
              disabled={readOnly}
              onChange={(v) => set({ vehicleId: v })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <LookupSelect
              label="Client"
              value={instance.clientId}
              options={lookups.clients}
              disabled={readOnly}
              onChange={(v) => set({ clientId: v })}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <LookupSelect
              label="Shift type"
              value={instance.shiftTypeId}
              options={lookups.shiftTypes}
              disabled={readOnly}
              onChange={(v) => set({ shiftTypeId: v })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                labelId="status-label"
                label="Status"
                value={instance.status}
                onChange={(e) => set({ status: e.target.value })}
                disabled={readOnly}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Change reasons appear only once the value actually differs from what is stored. */}
        {(driverChanged || vehicleChanged) && !readOnly && (
          <>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              {driverChanged && (
                <Grid item xs={12} sm={6}>
                  <LookupSelect
                    label="Driver change reason (required)"
                    value={driverReasonId}
                    options={lookups.driverChangeReasons}
                    disabled={false}
                    onChange={setDriverReasonId}
                  />
                </Grid>
              )}
              {vehicleChanged && (
                <Grid item xs={12} sm={6}>
                  <LookupSelect
                    label="Vehicle change reason (required)"
                    value={vehicleReasonId}
                    options={lookups.vehicleChangeReasons}
                    disabled={false}
                    onChange={setVehicleReasonId}
                  />
                </Grid>
              )}
            </Grid>
          </>
        )}

        {!readOnly && (
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="contained" sx={SAVE_BUTTON_SX} onClick={save} disabled={isSaving}>
              {isSaving ? "Saving…" : "Save Shift"}
            </Button>
          </Stack>
        )}
      </Paper>

      {instance.subShifts.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Sub-shifts
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
            Other shifts on this template and date — usually a driver swap part-way through.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Shift #</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Driver</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {instance.subShifts.map((sub) => (
                <TableRow
                  key={sub.yardDailyId}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate(
                      `/shifts/logistrics/daily/${sub.yardDailyId}?hub=${params.get("hub") ?? ""}&date=${params.get("date") ?? ""}`
                    )
                  }
                >
                  <TableCell>{sub.yardDailyId}</TableCell>
                  <TableCell>{sub.timeStart}</TableCell>
                  <TableCell>{sub.timeEnd}</TableCell>
                  <TableCell>{sub.driverName}</TableCell>
                  <TableCell>{sub.unitNumber}</TableCell>
                  <TableCell>{sub.status}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

const LookupSelect = ({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: number | null;
  options: { id: number; name: string }[];
  disabled: boolean;
  onChange: (value: number | null) => void;
}) => {
  const labelId = `${label.replace(/\s+/g, "-").toLowerCase()}-label`;
  return (
    <FormControl size="small" fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={value ?? 0}
        onChange={(e) => onChange(Number(e.target.value) || null)}
        disabled={disabled}
      >
        <MenuItem value={0}>—</MenuItem>
        {options.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default YardDailyDetail;
